import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DeviceInfo } from '@/types/api';
import { cropApi } from '@/services/cropApi';
import DeviceTable from './components/DeviceTable';
import Toast from './components/Toast';
import type { ToastMessage } from './components/Toast';

const AUTO_REFRESH_INTERVAL = 30000; // 30초

let toastIdCounter = 0;

export default function DevicesPage() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadDevices = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsRefreshing(true);
    try {
      const res = await cropApi.getDeviceList();
      if (res.result === 'success' && Array.isArray(res.devices)) {
        setDevices(res.devices);
        setError('');
      } else {
        setError(res.message || '장비 목록을 불러오지 못했습니다.');
      }
    } catch {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
      if (showSpinner) setIsRefreshing(false);
    }
  }, []);

  // 초기 로드 + 자동 갱신
  useEffect(() => {
    loadDevices();

    autoRefreshRef.current = setInterval(() => {
      loadDevices();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [loadDevices]);

  const handleRefresh = useCallback(() => {
    loadDevices(true);
  }, [loadDevices]);

  const handleToast = useCallback((toast: ToastMessage) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const handleRemoveToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleGoDashboard = useCallback(() => {
    const deviceId = localStorage.getItem('device_id');
    if (deviceId) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f4f8f5' }}>
      {/* Header */}
      <header
        className="w-full sticky top-0 z-40 shadow-sm"
        style={{ background: 'linear-gradient(90deg, #1B4F72 0%, #145a32 100%)' }}
      >
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg overflow-hidden bg-white/15">
              <img
                src="https://public.readdy.ai/ai/img_res/9afcb98f-92d6-44dd-b7d6-8d5092554fc9.png"
                alt="Logo"
                className="w-6 h-6 object-contain"
              />
            </div>
            <div>
              <h1 className="text-white font-bold text-sm md:text-base leading-tight">
                장비 관리
              </h1>
              <p className="text-green-300 text-xs leading-tight hidden sm:block">
                수동 촬영 &amp; 장비 상태 모니터링
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleGoDashboard}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-all hover:bg-white/20 border border-white/30"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-dashboard-line"></i>
              </div>
              대시보드
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('device_id');
                navigate('/');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-all hover:bg-white/20 border border-white/30"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-line"></i>
              </div>
              로그아웃
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-screen-xl mx-auto px-4 md:px-6 py-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-gray-800">장비 목록</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              총 <strong className="text-gray-700">{devices.length}</strong>대의 장비
              {devices.filter((d) => d.online).length > 0 && (
                <span className="text-green-600">
                  {' '}({devices.filter((d) => d.online).length}대 온라인)
                </span>
              )}
            </p>
          </div>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all border"
            style={{
              backgroundColor: '#fff',
              borderColor: '#e5e7eb',
              color: '#374151',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i
                className={`ri-refresh-line ${isRefreshing ? 'animate-spin' : ''}`}
              ></i>
            </div>
            새로고침
          </button>
        </div>

        <DeviceTable
          devices={devices}
          loading={loading}
          error={error}
          onRefresh={handleRefresh}
          onToast={handleToast}
        />
      </main>

      {/* Footer */}
      <footer className="mt-8 py-5 text-center" style={{ backgroundColor: '#1B4F72' }}>
        <p className="text-white/60 text-xs">
          © 2026 Rice Crop Monitoring System
        </p>
      </footer>

      {/* Toast */}
      <Toast toasts={toasts} onRemove={handleRemoveToast} />

      {/* Toast Slide-in Animation */}
      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}